import HeaderSm from 'components/headers/sm'
import type { LiabilityFormLayout } from '../../types'
import { liabilityAmountLayout } from '../amount'
import { liabilityInterestRateLayout } from '../interestRate'
import { liabilityIssueDateLayout } from '../issueDate'
import { liabilityPaymentScheduleLayout } from '../paymentSchedule'
import { prettyNumber } from '@/utils/number/format'
import { useUserSettings } from '@/modules/settings/hooks/useUserSettings'
import { alreadyAmortizedLayout } from '../alreadyAmortized'
import { loanTermLayout } from '../loanTermLayout'
import { separator } from '../separator'
import { dateToHumanReadable, EDateFormat } from '@/utils/formatting/date'
import { liabilityQuantityPercentLayout } from '../quantityPercent'
import { tween } from '@/utils/animation/tween'
import { ref } from 'vue'
import { calculateLiabilityExpirationDate } from 'findef'
import { investmentsLayout } from '../investments'

export const step2Layout = (options: { withRelatedInvestments?: boolean } = { withRelatedInvestments: true }): LiabilityFormLayout => {
  return {
    children: [
      {
        component: () => () => (
          <HeaderSm
            title="Loan Information"
            subtitle="Enter more specific information about to keep track of value."
          />
        )
      },
      {
        attributes: {
          class: 'grid grid-auto-rows gap-12'
        },
        children: [
          {
            children: [
              {
                attributes: {
                  class: 'grid grid-cols-1 md:grid-cols-2 gap-4'
                },
                children: [liabilityAmountLayout(), liabilityInterestRateLayout()]
              },
              {
                attributes: {
                  class: 'grid grid-cols-1 md:grid-cols-2 gap-4'
                },
                children: [
                  liabilityQuantityPercentLayout(),
                  {
                    component: () => {
                      const settings = useUserSettings();
                      const myAmount = ref<number>(0);
                      const w = tween(myAmount, { duration: 0.5 });
                      return (props) => {
                        myAmount.value = props.data?.value?.myAmount?.value || 0;
                        const myAmountFormatted = prettyNumber(w.value, {
                          american: true,
                          maxDecimals: 2,
                          zeroIsInteger: true,
                          suffix: props.data?.value?.myAmount?.type || settings.currency.value
                        })

                        return <div class="h-[42px] w-full px-3 py-2 bg-gray-100 border border-gray-300 flex-col justify-center items-center inline-flex">
                          <div class="text-center text-gray-500 text-[10px] font-normal font-['Inter'] leading-[15px]">
                            My Borrowed Amount
                          </div>
                          <div class="text-center text-[#111928] text-sm font-medium font-['Inter'] leading-[21px]">
                            {myAmountFormatted}
                          </div>
                        </div>
                      }
                    }
                  }
                ]
              },
              {
                attributes: {
                  class: 'grid grid-cols-1 md:grid-cols-2 gap-4'
                },
                children: [liabilityIssueDateLayout(), loanTermLayout()]
              },
              {
                component: () => {
                  return (props) => {
                    const maturityDate = calculateLiabilityExpirationDate(props.state.value) || new Date();
                    return <div style={{ marginTop: '-16px' }}>
                      <div>
                        <span class="text-sm text-gray-500">The maturity date for this loan is</span>{' '}
                        <span class="text-sm font-semibold text-gray-500">{dateToHumanReadable(maturityDate, EDateFormat.DAY_MONTH_YEAR)}</span>
                      </div>
                    </div>
                  }
                }
              },
              alreadyAmortizedLayout(),
              liabilityPaymentScheduleLayout(),
              options.withRelatedInvestments ? separator() : null,
              options.withRelatedInvestments ? investmentsLayout() : null
            ]
          },
          {
            component: () => () => (
              <HeaderSm title="Loan Value" subtitle="The current value for your share of the loan" />
            )
          }
        ]
      },
      {
        component: () => {
          const settings = useUserSettings();
          const remainingDebt = ref<number>(0);
          const remainingInterest = ref<number>(0);
          const remainingDebtTweened = tween(remainingDebt, { duration: 0.5 });
          const remainingInterestTweened = tween(remainingInterest, { duration: 0.5 });

          return (props) => {
            remainingDebt.value = props.data?.value?.remainingDebt?.value || 0;
            remainingInterest.value = props.data?.value?.remainingInterest?.value || 0;

            const remainingDebtFormatted = prettyNumber(remainingDebtTweened.value, {
              american: true,
              maxDecimals: 2,
              zeroIsInteger: true,
              suffix: props.data?.value?.remainingDebt?.type || settings.currency.value
            })

            const remainingInterestFormatted = prettyNumber(
              remainingInterestTweened.value,
              {
                american: true,
                maxDecimals: 2,
                zeroIsInteger: true,
                suffix: props.data?.value?.remainingInterest?.type || settings.currency.value
              }
            )

            return (
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="h-[69px] px-3 py-1 bg-gray-100 border border-gray-300 flex-col justify-center items-center inline-flex">
                  <div class="text-center text-gray-500 text-[10px] font-normal font-['Inter'] leading-[15px]">
                    My Remaining Amortization
                  </div>
                  <div class="text-center text-[#111928] text-sm font-medium font-['Inter'] leading-[21px]">
                    {remainingDebtFormatted}
                  </div>
                </div>
                <div class="h-[69px] px-3 py-1 bg-gray-100 border border-gray-300 flex-col justify-center items-center inline-flex">
                  <div class="text-center text-gray-500 text-[10px] font-normal font-['Inter'] leading-[15px]">
                    My Remaining Interest
                  </div>
                  <div class="text-center text-[#f05252] text-sm font-medium font-['Inter'] leading-[21px]">
                    {remainingInterestFormatted}
                  </div>
                </div>
              </div>
            )
          }
        }
      }
    ]
  }
}
